import type { Element, Root } from "hast";
import { urlAttributes } from "html-url-attributes";
import { defaultUrlTransform } from "react-markdown";
import type { Plugin } from "unified";
import { visit } from "unist-util-visit";

/**
 * URL-sanitization pass mirroring `react-markdown`'s built-in policy (§6.1).
 *
 * `react-markdown` applies `defaultUrlTransform` (dropping unsafe protocols such
 * as `javascript:`) to URL-bearing attributes before rendering; converting HAST
 * directly with `hast-util-to-jsx-runtime` does not. To keep v2's trust boundary
 * identical to v1, this pass runs the same `defaultUrlTransform` over the same
 * `html-url-attributes` set (`href`, `src`, …). Citation anchors use `href="#"`
 * and are left unchanged.
 */
export const rehypeSanitizeUrls: Plugin<[], Root> = function () {
  return function (tree: Root): void {
    visit(tree, "element", (element: Element) => {
      for (const attribute of Object.keys(urlAttributes)) {
        const applicableTags = urlAttributes[attribute];
        const appliesToTag =
          applicableTags === null || applicableTags.includes(element.tagName);
        if (!appliesToTag) continue;

        const value = element.properties[attribute];
        if (typeof value !== "string") continue;

        element.properties[attribute] = defaultUrlTransform(value);
      }
    });
  };
};
